import lodash from 'lodash'

import {absoluteBoundsToRelativeOffsets} from '@base/dom-utils/position'

import rateStrategy, {rateTarget} from './rateStrategy'
import {resolveRectangles} from './resolvers'
import type {
  Offset,
  StrategyFn,
  StrategyOptions,
  RatingOptions,
  StyleResult,
  CalculateOptions,
  CalculateResult,
  CalculateStrategiesOptions,
  CalculateStrategiesResult,
} from './types'

const IMPORTANT_RESULT_KEYS = [
  'strategy',
  'rating',
  'left',
  'top',
  'placement',
  'maxWidth',
  'maxHeight',
  'minWidth',
  'minHeight',
  'width',
  'height',
  'shiftedX',
  'shiftedY',
  'anchorX',
  'anchorY',
  'style',
]

export function areResultsEqual(a?: any | null, b?: any | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  for (const key of IMPORTANT_RESULT_KEYS) {
    const valueA = a[key]
    const valueB = b[key]
    // use isEqual only on objects for performance
    if (typeof valueA === 'object' && valueA) {
      if (!lodash.isEqual(valueA, valueB)) return false
    } else if (valueA !== valueB) {
      return false
    }
  }
  return true
}

export function runStrategy(
  strategy: StrategyFn,
  options: StrategyOptions & RatingOptions
): CalculateStrategiesResult {
  const result = {
    strategy,
    rectangles: {
      element: options.elementRect,
      target: options.targetRect,
      visible: options.visibleRect,
      container: options.containerRect,
      targetVisible: options.targetVisibleRect,
    },
    ...strategy(options),
    rating: 0,
    targetRating: 0,
  }
  const rating = rateStrategy(result, options)
  const targetRating = rateTarget(result, options)
  return {
    ...result,
    rating,
    targetRating,
  }
}

export function electStrategy({
  strategies,
  ...options
}: CalculateStrategiesOptions): CalculateStrategiesResult | undefined | null {
  const results: CalculateStrategiesResult[] = []
  for (const strategy of strategies) {
    const result = runStrategy(strategy, options)
    if (result.rating >= 1) return result
    if (result.rating > 0) results.push(result)
  }
  // we're stable sorting on negative scale and we want first strategies to win if equal
  return lodash.last(lodash.sortBy(results.reverse(), 'rating'))
}

export function calculateStrategies(
  options: CalculateStrategiesOptions
): CalculateStrategiesResult {
  if (!options.targetRect) throw new Error('targetRect is required')
  if (!options.elementRect) throw new Error('elementRect is required')

  const {strategies, lastResult, minRating = 1} = options
  const strategy: any = (lastResult && lastResult.strategy) || strategies[0]
  if (!strategy) throw new Error('No tether strategy provided!')

  const result = runStrategy(strategy, options)
  if (
    result.rating < minRating &&
    strategies &&
    (strategies.length > 1 || strategies[0] !== strategy)
  ) {
    const newStrategy = electStrategy(options)
    if (
      newStrategy &&
      (newStrategy.rating >= 1 || newStrategy.rating - result.rating > 0.5)
    ) {
      return newStrategy
    }
  }
  return result
}

function buildStyleBounds(pos: Offset, result: CalculateStrategiesResult) {
  switch (result.placement) {
    case 'top':
      // When element is positioned above with "top", it's pinned to that position.
      // When it grows, it'll obscure the target for a single frame, when it'll be positioned higher.
      // By pinning it to bottom offset, the element can grow freely
      return {
        left: Math.round(pos.left),
        bottom: Math.round(pos.top + result.rectangles.element.height),
      }
    case 'left':
      return {
        right: Math.round(pos.left + result.rectangles.element.width),
        top: Math.round(pos.top),
      }
    default:
      return {
        left: Math.round(pos.left),
        top: Math.round(pos.top),
      }
  }
}

// Positioning of elements with transform is much faster, but every transform introduces a new depth stack
// This makes it harder to set proper z-index on positioned element, as the z-index has to be set on positioning container,
// not the element itself.
// TODO I'm leaving this function here, unused, for future consideration. When we cleanup z-indexes, it'll be much more usable
// eslint-disable-next-line unused-imports/no-unused-vars
function buildStyleTransform(pos, result: CalculateStrategiesResult) {
  switch (result.placement) {
    case 'top':
      return `translateX(${Math.round(pos.left)}px) translateY(${Math.round(
        pos.top + result.rectangles.element.height
      )}px) translateY(-100%)`
    case 'left':
      return `translateX(${Math.round(
        pos.left + result.rectangles.element.width
      )}px) translateX(-100%) translateY(${Math.round(pos.top)}px)`
    default:
      return `translate(${Math.round(pos.left)}px, ${Math.round(pos.top)}px)`
  }
}

export function calculateStyle(
  positioner: Element,
  result: CalculateStrategiesResult
): StyleResult {
  if (!positioner) throw new Error('positioner is required')
  const absolutePos = {left: result.left, top: result.top}
  // @ts-ignore
  const style: StyleResult = {
    ...absoluteBoundsToRelativeOffsets(
      positioner,
      buildStyleBounds(absolutePos, result)
    ),
    // see above
    // transform: buildStyleTransform(absoluteBoundsToRelativeOffsets(positioner, absolutePos), result),
  }
  if (result.maxWidth !== undefined)
    style.maxWidth = Math.round(result.maxWidth)
  if (result.maxHeight !== undefined)
    style.maxHeight = Math.round(result.maxHeight)
  if (result.minWidth !== undefined)
    style.minWidth = Math.round(result.minWidth)
  if (result.minHeight !== undefined)
    style.minHeight = Math.round(result.minHeight)
  if (result.width !== undefined) style.width = Math.round(result.width)
  if (result.height !== undefined) style.height = Math.round(result.height)
  return style
}

export function calculate(options: CalculateOptions): CalculateResult {
  const {positioner} = options
  const rectangles = resolveRectangles(options)
  if (!rectangles) throw new Error('element & target are required')
  const result = calculateStrategies({...rectangles, ...options})
  const style = calculateStyle(positioner, result)
  return {
    ...result,
    style,
  }
}

export default calculate
